import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  HelpCircle,
  MessageCircle,
  Loader2,
  Sparkles,
  Mail,
  Phone,
  ArrowRight,
  Shield,
  Clock,
  HeadphonesIcon,
} from "lucide-react";
import { ShopFaq } from "@/types/shop";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchShopFaqs } from "@/store/slices/shopSlice";
import { fetchAllShopSettings } from "@/store/slices/shopSettingsSlice";

const ShopFAQSection = () => {
  const dispatch = useAppDispatch();
  const { data: faqs, loading } = useAppSelector((state) => state.shop.faqs);
  const { data: settingsList } = useAppSelector((state) => state.shopSettings);
  const currentSettings = settingsList && settingsList.length > 0 ? settingsList[0] : null;

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchShopFaqs());
    dispatch(fetchAllShopSettings());
  }, [dispatch]);

  const phone = (currentSettings?.shopPhoneNumber && currentSettings.shopPhoneNumber[0]) || "+92 313 7938402";
  const email = currentSettings?.shopEmail || "shophubmicroo@gmail.com";

  const getWhatsAppUrl = () => {
    const wa = currentSettings?.socialLinks?.whatsapp;
    if (wa && wa.trim()) {
      if (wa.startsWith("http")) return wa;
      const clean = wa.replace(/[^0-9]/g, "");
      return `https://wa.me/${clean}`;
    }
    const cleanPhone = phone.replace(/[^0-9]/g, "");
    return `https://wa.me/${cleanPhone}`;
  };

  if (!loading && (!faqs || faqs.length === 0)) return null;

  return (
    <section className="py-24 bg-white overflow-hidden relative" id="shop-faq">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(147,51,234,0.06),transparent)]" />
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `linear-gradient(#9333ea 1px, transparent 1px), linear-gradient(90deg, #9333ea 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
        }}
      />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-100/60 rounded-full blur-[100px] -mr-64 -mt-64 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-100/60 rounded-full blur-[100px] -ml-64 -mb-64 pointer-events-none" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-purple-50 border border-purple-100 text-purple-600 text-xs font-black tracking-widest mb-6"
          >
            <HelpCircle size={14} />
            KNOWLEDGE CENTER
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-slate-900 mb-5 tracking-tight"
          >
            Everything You{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500">
              Need to Know
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="text-slate-500 text-lg max-w-2xl mx-auto font-medium leading-relaxed"
          >
            Curated answers to our most common inquiries. If your question isn't
            covered, our experts are just a click away.
          </motion.p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3 mb-20">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader2 className="animate-spin text-purple-600" size={40} />
              <p className="text-purple-500/60 font-bold tracking-widest text-xs">
                LOADING FAQs…
              </p>
            </div>
          ) : (
            faqs.map((faq: ShopFaq, index: number) => (
              <motion.div
                key={faq._id || index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <button
                  onClick={() =>
                    setActiveIndex(activeIndex === index ? null : index)
                  }
                  className={`w-full cursor-pointer text-left p-6 md:p-8 rounded-lg transition-all duration-300 border ${
                    activeIndex === index
                      ? "bg-white border-purple-200 shadow-[0_8px_40px_rgba(147,51,234,0.12)] ring-1 ring-purple-100"
                      : "bg-gray-50/80 border-gray-100 hover:border-purple-200 hover:bg-white hover:shadow-md"
                  }`}
                >
                  <div className="flex justify-between items-center gap-4">
                    {/* Index number + question */}
                    <div className="flex items-center gap-4">
                      <span
                        className={`flex-none w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black transition-all duration-300 ${
                          activeIndex === index
                            ? "bg-gradient-to-br from-purple-600 to-pink-500 text-white shadow-md"
                            : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3
                        className={`text-base md:text-lg font-bold tracking-tight transition-all duration-300 ${
                          activeIndex === index
                            ? "bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] bg-clip-text text-transparent"
                            : "text-slate-800"
                        }`}
                      >
                        {faq.question}
                      </h3>
                    </div>

                    {/* Chevron */}
                    <div
                      className={`flex-none w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        activeIndex === index
                          ? "bg-purple-600 text-white rotate-180 shadow-md shadow-purple-200"
                          : "bg-white text-slate-400 border border-slate-200"
                      }`}
                    >
                      <ChevronDown size={18} />
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.35,
                          ease: [0.04, 0.62, 0.23, 0.98],
                        }}
                        className="overflow-hidden"
                      >
                        <div
                          className="pt-5 mt-5 border-t border-purple-100 text-slate-600 leading-relaxed text-base font-medium prose prose-purple max-w-none"
                          dangerouslySetInnerHTML={{ __html: faq.answer }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            ))
          )}
        </div>

        {/* ── Redesigned 24/7 Support CTA ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-lg overflow-hidden bg-gradient-to-b from-purple-50/90 via-pink-50/30 to-white border border-purple-100 p-6 sm:p-10 md:p-14 shadow-xl shadow-purple-500/5 text-center"
        >
          {/* Ambient Lighting Accents */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-purple-200/40 via-pink-100/30 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-pink-200/30 via-purple-100/20 to-transparent rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            {/* Top Badge */}
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-white border border-purple-200 text-purple-700 text-xs font-bold tracking-wider shadow-xs">
                <Sparkles size={13} className="text-purple-600" />
                24/7 EXPERT SUPPORT
              </span>
            </div>

            {/* Heading & Subtitle */}
            <div className="space-y-2.5">
              <h3 className="text-2xl sm:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight">
                Still Have Questions?{" "}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 font-extrabold mt-1">
                  We're Here to Help
                </span>
              </h3>
              <p className="text-gray-600 text-xs sm:text-base font-normal max-w-lg mx-auto leading-relaxed">
                Our expert support team is available around the clock to assist
                you with anything — from order tracking to product inquiries.
              </p>
            </div>

            {/* Trust Badges Row */}
            <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3.5 pt-1 pb-2">
              {[
                {
                  icon: Clock,
                  label: "Avg. reply in 2 min",
                  color: "text-purple-600 bg-purple-50 border-purple-100",
                },
                {
                  icon: Shield,
                  label: "Secure & Confidential",
                  color: "text-emerald-600 bg-emerald-50 border-emerald-100",
                },
                {
                  icon: HeadphonesIcon,
                  label: "Expert Support Team",
                  color: "text-pink-600 bg-pink-50 border-pink-100",
                },
              ].map(({ icon: Icon, label, color }) => (
                <div
                  key={label}
                  className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold border shadow-xs ${color}`}
                >
                  <Icon size={13} />
                  <span className="text-gray-800">{label}</span>
                </div>
              ))}
            </div>

            {/* CTA Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <motion.button
                onClick={() => window.open(getWhatsAppUrl(), "_blank")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto flex items-center justify-center gap-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-bold text-xs sm:text-sm px-6 py-3.5 rounded-xl shadow-md shadow-emerald-600/20 hover:shadow-lg transition-all cursor-pointer"
              >
                <MessageCircle size={18} />
                <span>Contact on WhatsApp</span>
                <ArrowRight size={15} />
              </motion.button>

              <motion.button
                onClick={() => (window.location.href = `mailto:${email}`)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-xs sm:text-sm px-6 py-3.5 rounded-xl shadow-md shadow-purple-500/20 hover:shadow-lg transition-all cursor-pointer"
              >
                <Mail size={17} />
                <span>Email Support</span>
              </motion.button>
            </div>

            {/* Direct Phone Helpline Card */}
            <div className="pt-2">
              <a
                href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-purple-200/80 text-xs text-gray-600 hover:text-purple-700 hover:border-purple-300 transition shadow-xs cursor-pointer"
              >
                <Phone size={13} className="text-purple-600" />
                <span>Or call us:</span>
                <span className="font-bold text-gray-900">{phone}</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ShopFAQSection;
